import React, { useState, useEffect, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Row, Col, Card, CardBody, CardTitle, Button, Spinner, Alert,
  Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input,
  Badge, Table,
} from "reactstrap"
import Switch from "react-switch"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import axiosClient from "../../helpers/axiosClient"
import XLSX from "xlsx-js-style"
import { utils, read } from "xlsx"
import "./Empresas.scss"

const TIPOS_DOCUMENTO = {
  "01": "Factura electrónica de Venta",
  "02": "Factura electrónica de venta - exportación",
  "03": "Instrumento electrónico de transmisión – tipo 03",
  "04": "Factura electrónica de Venta - tipo 04",
  "91": "Nota Crédito",
  "92": "Nota Débito",
}

/* ── styles ── */
const labelStyle = { fontWeight: 600, fontSize: 13, color: "#495057", marginBottom: 4 }
const inputStyle = { borderRadius: 8, fontSize: 13 }
const sectionTitle = { display: "flex", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 600, color: "#343a40", marginBottom: 12 }

const emptySucursal = {
  codigo: "",
  nombre: "",
  direccion: "",
  telefono: "",
  ciudad: "",
  departamento: "",
  storeId: "",
  brandId: "",
  esPrincipal: false,
}

const Sucursales = ({ setBreadcrumbItems, user }) => {
  document.title = "Sucursales | LS-DA - Softlogy"

  const { id } = useParams() // empresaId
  const navigate = useNavigate()

  const [sucursales, setSucursales] = useState([])
  const [empresaNombre, setEmpresaNombre] = useState("")
  const [loading, setLoading] = useState(true)
  const [alert, setAlert] = useState({ show: false, message: "", variant: "" })

  // Modal states
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState("create") // "create" | "edit"
  const [formData, setFormData] = useState({ ...emptySucursal })
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  // Dropdowns
  const [departamentos, setDepartamentos] = useState([])
  const [ciudades, setCiudades] = useState([])
  const [allMunicipios, setAllMunicipios] = useState([])

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  // Import modal
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [importRows, setImportRows] = useState([])
  const [importing, setImporting] = useState(false)
  const [importDone, setImportDone] = useState(false)

  // Prefijo assignments
  const [prefijosAsignados, setPrefijosAsignados] = useState([])
  const [resoluciones, setResoluciones] = useState([])

  // ─── XML Parser ───
  const fetchXmlList = async (url) => {
    const res = await fetch(url)
    if (!res.ok) {
      console.error(`[fetchXmlList] HTTP ${res.status} for ${url}`)
      return []
    }
    const contentType = res.headers.get("content-type") || ""
    const text = await res.text()
    // Detect SPA redirect: Amplify returns index.html instead of XML
    if (contentType.includes("text/html") || text.trimStart().startsWith("<!doctype") || text.trimStart().startsWith("<!DOCTYPE")) {
      console.error(`[fetchXmlList] Got HTML instead of XML for ${url} — check Amplify rewrite rules`)
      return []
    }
    const parser = new DOMParser()
    const xml = parser.parseFromString(text, "text/xml")
    const parseError = xml.querySelector("parsererror")
    if (parseError) {
      console.error(`[fetchXmlList] XML parse error for ${url}:`, parseError.textContent)
      return []
    }
    const rows = xml.querySelectorAll("SimpleCodeList > Row")
    const items = []
    rows.forEach(row => {
      const vals = row.querySelectorAll("Value")
      let code = "", name = ""
      vals.forEach(v => {
        const ref = v.getAttribute("ColumnRef")
        const sv = v.querySelector("SimpleValue")?.textContent || ""
        if (ref === "code") code = sv
        else if (ref === "name") name = sv
      })
      if (code) items.push({ code, name })
    })
    return items
  }

  // ─── Fetch municipios filtered ───
  const fetchCiudades = useCallback((depCode) => {
    if (!depCode || !allMunicipios.length) { setCiudades([]); return }
    const filtered = allMunicipios.filter(m => m.code.startsWith(depCode))
    setCiudades(filtered)
  }, [allMunicipios])

  // ─── Fetch sucursales ───
  const fetchSucursales = useCallback(async () => {
    setLoading(true)
    try {
      const res = await axiosClient.get(`/empresas/${id}/sucursales`)
      setSucursales(res.data?.data || res.data || [])
    } catch (err) {
      setAlert({ show: true, message: "Error al cargar sucursales: " + (err.response?.data?.message || err.message), variant: "danger" })
      setSucursales([])
    } finally {
      setLoading(false)
    }
  }, [id])

  // ─── Fetch empresa name ───
  const fetchEmpresa = useCallback(async () => {
    try {
      const res = await axiosClient.get(`/empresas/${id}`)
      const emp = res.data?.data || res.data
      setEmpresaNombre(emp?.nombre || "")
    } catch (e) { /* ignore */ }
  }, [id])

  // ─── Fetch prefijo-sucursal assignments ───
  const fetchAsignaciones = useCallback(async () => {
    try {
      const res = await axiosClient.get(`/empresas/${id}/prefijos-sucursales`)
      setPrefijosAsignados(res.data?.data || res.data || [])
    } catch (e) { setPrefijosAsignados([]) }
  }, [id])

  // ─── Init ───
  useEffect(() => {
    const greeting = user ? `Bienvenido, ${user.nombre || "Usuario"}` : "Bienvenido"
    setBreadcrumbItems(greeting, [
      { title: "LS-DA", link: "#" },
      { title: "Empresas", link: "/portal/consultar-empresas" },
      { title: "Sucursales", link: "#" },
    ])
    fetchSucursales()
    fetchEmpresa()
    fetchAsignaciones()
    // Fetch resoluciones for tipo info
    axiosClient.get(`/empresas/${id}/resoluciones`)
      .then(res => setResoluciones(res.data?.data || res.data || []))
      .catch(() => { })
  }, []) // eslint-disable-line

  useEffect(() => {
    const loadDropdowns = async () => {
      try { setDepartamentos(await fetchXmlList("/listas/Departamentos-2.1.xml")) } catch (e) { }
      try { setAllMunicipios(await fetchXmlList("/listas/Municipio-2.1.xml")) } catch (e) { }
    }
    loadDropdowns()
  }, [])

  // ─── Modal handlers ───
  const openCreateModal = () => {
    setModalMode("create")
    setFormData({ ...emptySucursal })
    setEditingId(null)
    setModalOpen(true)
  }

  const openEditModal = (suc) => {
    setModalMode("edit")
    setFormData({
      codigo: suc.codigo || "",
      nombre: suc.nombre || "",
      direccion: suc.direccion || "",
      telefono: suc.telefono || "",
      ciudad: suc.ciudad || "",
      departamento: suc.departamento || "",
      storeId: suc.storeId || "",
      brandId: suc.brandId || "",
      esPrincipal: suc.esPrincipal || false,
    })
    setEditingId(suc.id)
    setModalOpen(true)
    // Load cities for the department
    const dep = departamentos.find(d => d.name === suc.departamento)
    if (dep) fetchCiudades(dep.code)
  }

  const closeModal = () => {
    setModalOpen(false)
    setFormData({ ...emptySucursal })
    setEditingId(null)
  }

  const setFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  // ─── Create sucursal ───
  const handleCreate = async () => {
    if (!formData.codigo || !formData.nombre) {
      setAlert({ show: true, message: "Código y Nombre son obligatorios", variant: "warning" })
      return
    }
    setSaving(true)
    try {
      await axiosClient.post(`/empresas/${id}/sucursales`, formData)
      setAlert({ show: true, message: "Sucursal creada correctamente", variant: "success" })
      closeModal()
      fetchSucursales()
    } catch (err) {
      setAlert({ show: true, message: "Error al crear: " + (err.response?.data?.message || err.message), variant: "danger" })
    } finally {
      setSaving(false)
    }
  }

  // ─── Update sucursal ───
  const handleUpdate = async () => {
    if (!formData.codigo || !formData.nombre) {
      setAlert({ show: true, message: "Código y Nombre son obligatorios", variant: "warning" })
      return
    }
    setSaving(true)
    try {
      await axiosClient.put(`/empresas/${id}/sucursales/${editingId}`, formData)
      setAlert({ show: true, message: "Sucursal actualizada correctamente", variant: "success" })
      closeModal()
      fetchSucursales()
    } catch (err) {
      setAlert({ show: true, message: "Error al actualizar: " + (err.response?.data?.message || err.message), variant: "danger" })
    } finally {
      setSaving(false)
    }
  }

  // ─── Delete sucursal ───
  const handleDelete = async (sucId, nombre) => {
    if (!window.confirm(`¿Está seguro de eliminar la sucursal "${nombre}"?`)) return
    setDeletingId(sucId)
    try {
      await axiosClient.delete(`/empresas/${id}/sucursales/${sucId}`)
      setAlert({ show: true, message: "Sucursal eliminada correctamente", variant: "success" })
      fetchSucursales()
    } catch (err) {
      setAlert({ show: true, message: "Error al eliminar: " + (err.response?.data?.message || err.message), variant: "danger" })
    } finally {
      setDeletingId(null)
    }
  }

  // ─── Export Excel ───
  const handleExportExcel = () => {
    if (sucursales.length === 0) return

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12, name: "Calibri" },
      fill: { fgColor: { rgb: "2B3A67" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "CCCCCC" } },
        bottom: { style: "thin", color: { rgb: "CCCCCC" } },
        left: { style: "thin", color: { rgb: "CCCCCC" } },
        right: { style: "thin", color: { rgb: "CCCCCC" } },
      },
    }
    const cellBorder = {
      top: { style: "thin", color: { rgb: "DEE2E6" } },
      bottom: { style: "thin", color: { rgb: "DEE2E6" } },
      left: { style: "thin", color: { rgb: "DEE2E6" } },
      right: { style: "thin", color: { rgb: "DEE2E6" } },
    }
    const evenRow = { fill: { fgColor: { rgb: "F8F9FA" } }, border: cellBorder, font: { name: "Calibri", sz: 11 }, alignment: { vertical: "center" } }
    const oddRow = { fill: { fgColor: { rgb: "FFFFFF" } }, border: cellBorder, font: { name: "Calibri", sz: 11 }, alignment: { vertical: "center" } }

    // Title row
    const titleRow = [{
      v: `Sucursales - ${empresaNombre}`, s: {
        font: { bold: true, sz: 16, color: { rgb: "2B3A67" }, name: "Calibri" },
        alignment: { horizontal: "center" },
      }
    }]
    const dateRow = [{
      v: `Generado: ${new Date().toLocaleDateString("es-CO", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}`, s: {
        font: { italic: true, sz: 10, color: { rgb: "6C757D" }, name: "Calibri" },
        alignment: { horizontal: "center" },
      }
    }]

    const headers = ["Código", "Nombre", "Dirección", "Teléfono", "Departamento", "Ciudad", "Store ID", "Brand ID", "Principal", "Prefijos Asignados", "Tipo Documento", "Estado"]
    const headerRow = headers.map(h => ({ v: h, s: headerStyle }))

    const dataRows = sucursales.map((suc, i) => {
      const style = i % 2 === 0 ? evenRow : oddRow
      const principalStyle = { ...style, alignment: { ...style.alignment, horizontal: "center" } }
      // Get assigned prefijos for this sucursal
      const assigned = prefijosAsignados.filter(a => a.sucursalId === suc.id)
      const prefijosText = assigned.length > 0 ? assigned.map(a => a.prefijo).join(", ") : "Sin asignar"
      const tiposText = assigned.length > 0 ? assigned.map(a => {
        const res = resoluciones.find(r => r.prefijo === a.prefijo)
        return res?.tipoDocumento ? `${a.prefijo}: ${res.tipoDocumento} - ${TIPOS_DOCUMENTO[res.tipoDocumento] || res.tipoDocumento}` : `${a.prefijo}: Sin tipo`
      }).join(", ") : "—"
      return [
        { v: suc.codigo || "", s: { ...style, font: { ...style.font, bold: true } } },
        { v: suc.nombre || "", s: style },
        { v: suc.direccion || "", s: style },
        { v: suc.telefono || "", s: style },
        { v: suc.departamento || "", s: style },
        { v: suc.ciudad || "", s: style },
        { v: suc.storeId || "", s: style },
        { v: suc.brandId || "", s: style },
        {
          v: suc.esPrincipal ? "✓ Sí" : "No", s: {
            ...principalStyle,
            font: { ...style.font, color: { rgb: suc.esPrincipal ? "198754" : "6C757D" }, bold: suc.esPrincipal },
          }
        },
        { v: prefijosText, s: style },
        { v: tiposText, s: { ...style, font: { ...style.font, sz: 10 } } },
        {
          v: suc.activo ? "Activo" : "Inactivo", s: {
            ...principalStyle,
            font: { ...style.font, color: { rgb: suc.activo ? "198754" : "DC3545" }, bold: true },
          }
        },
      ]
    })

    // Summary row
    const totalActivas = sucursales.filter(s => s.activo).length
    const summaryStyle = {
      font: { bold: true, sz: 11, color: { rgb: "2B3A67" }, name: "Calibri" },
      fill: { fgColor: { rgb: "E8ECF4" } },
      border: cellBorder,
      alignment: { vertical: "center" },
    }
    const summaryRow = [
      { v: "TOTAL", s: summaryStyle },
      { v: `${sucursales.length} sucursales`, s: summaryStyle },
      { v: "", s: summaryStyle },
      { v: "", s: summaryStyle },
      { v: "", s: summaryStyle },
      { v: "", s: summaryStyle },
      { v: "", s: summaryStyle },
      { v: "", s: summaryStyle },
      { v: `${sucursales.filter(s => s.esPrincipal).length} principal(es)`, s: { ...summaryStyle, alignment: { horizontal: "center", vertical: "center" } } },
      { v: "", s: summaryStyle },
      { v: "", s: summaryStyle },
      { v: `${totalActivas} activas`, s: { ...summaryStyle, alignment: { horizontal: "center", vertical: "center" } } },
    ]

    const wsData = [titleRow, dateRow, [], headerRow, ...dataRows, [], summaryRow]
    const ws = XLSX.utils.aoa_to_sheet(wsData)

    // Merge title and date across all columns
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 11 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 11 } },
    ]

    // Column widths
    ws["!cols"] = [
      { wch: 14 }, // Código
      { wch: 28 }, // Nombre
      { wch: 35 }, // Dirección
      { wch: 16 }, // Teléfono
      { wch: 22 }, // Departamento
      { wch: 22 }, // Ciudad
      { wch: 18 }, // Store ID
      { wch: 18 }, // Brand ID
      { wch: 12 }, // Principal
      { wch: 24 }, // Prefijos Asignados
      { wch: 40 }, // Tipo Documento
      { wch: 12 }, // Estado
    ]

    // Row heights
    ws["!rows"] = [{ hpx: 32 }, { hpx: 20 }, { hpx: 8 }, { hpx: 28 }, ...dataRows.map(() => ({ hpx: 24 }))]

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Sucursales")
    XLSX.writeFile(wb, `Sucursales_${empresaNombre || "empresa"}_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  // ─── Import Excel ───
  const handleDownloadTemplate = () => {
    const templateData = [
      ["codigo", "nombre", "direccion", "telefono", "departamento", "ciudad", "storeId", "brandId", "esPrincipal"],
      ["SUC-001", "Sucursal Principal", "Calle 80 #10-30", "3005551234", "Bogotá D.C.", "Bogotá D.C.", "STORE-001", "BRAND-001", "true"],
      ["SUC-002", "Sucursal Norte", "Cra 15 #100-20", "3109876543", "Antioquia", "Medellín", "STORE-002", "BRAND-002", "false"],
    ]
    const ws = XLSX.utils.aoa_to_sheet(templateData)
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11, name: "Calibri" },
      fill: { fgColor: { rgb: "2B3A67" } },
      alignment: { horizontal: "center" },
    }
    templateData[0].forEach((_, ci) => {
      const cell = ws[XLSX.utils.encode_cell({ r: 0, c: ci })]
      if (cell) cell.s = headerStyle
    })
    ws["!cols"] = [{ wch: 14 }, { wch: 28 }, { wch: 30 }, { wch: 16 }, { wch: 20 }, { wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 12 }]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Plantilla")
    XLSX.writeFile(wb, "Plantilla_Sucursales.xlsx")
  }

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    event.target.value = null

    try {
      const data = new Uint8Array(await file.arrayBuffer())
      const workbook = read(data, { type: "array" })
      const sheet = workbook.Sheets[workbook.SheetNames[0]]
      const jsonData = utils.sheet_to_json(sheet)

      if (jsonData.length === 0) {
        setAlert({ show: true, message: "El archivo está vacío", variant: "warning" })
        return
      }

      // Prepare rows with status
      const rows = jsonData.map((row, i) => ({
        index: i + 1,
        data: {
          codigo: String(row.codigo || row.Codigo || row.CODIGO || "").trim(),
          nombre: String(row.nombre || row.Nombre || row.NOMBRE || "").trim(),
          direccion: String(row.direccion || row.Direccion || row.DIRECCION || "").trim(),
          telefono: String(row.telefono || row.Telefono || row.TELEFONO || "").trim(),
          departamento: String(row.departamento || row.Departamento || row.DEPARTAMENTO || "").trim(),
          ciudad: String(row.ciudad || row.Ciudad || row.CIUDAD || "").trim(),
          storeId: String(row.storeId || row.StoreId || row.STOREID || row.store_id || "").trim(),
          brandId: String(row.brandId || row.BrandId || row.BRANDID || row.brand_id || "").trim(),
          esPrincipal: String(row.esPrincipal || row.EsPrincipal || row.principal || "").toLowerCase() === "true",
        },
        status: "pending", // pending | importing | success | error
        message: "",
      }))

      setImportRows(rows)
      setImportDone(false)
      setImportModalOpen(true)
    } catch (err) {
      setAlert({ show: true, message: "Error leyendo el archivo: " + err.message, variant: "danger" })
    }
  }

  const handleStartImport = async () => {
    setImporting(true)
    setImportDone(false)
    const updated = [...importRows]

    for (let i = 0; i < updated.length; i++) {
      // Validate required fields
      if (!updated[i].data.codigo || !updated[i].data.nombre) {
        updated[i].status = "error"
        updated[i].message = "Código y Nombre son obligatorios"
        setImportRows([...updated])
        continue
      }

      updated[i].status = "importing"
      setImportRows([...updated])

      try {
        await axiosClient.post(`/empresas/${id}/sucursales`, updated[i].data)
        updated[i].status = "success"
        updated[i].message = "Importada correctamente"
      } catch (err) {
        updated[i].status = "error"
        updated[i].message = err.response?.data?.message || err.message || "Error desconocido"
      }
      setImportRows([...updated])
    }

    setImporting(false)
    setImportDone(true)
    fetchSucursales()
  }

  const importSuccess = importRows.filter(r => r.status === "success").length
  const importErrors = importRows.filter(r => r.status === "error").length
  const importPending = importRows.filter(r => r.status === "pending" || r.status === "importing").length

  // ─── Pagination ───
  const totalPages = Math.ceil(sucursales.length / itemsPerPage)
  const paginatedItems = sucursales.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  return (
    <React.Fragment>
      {alert.show && (
        <Alert color={alert.variant} toggle={() => setAlert({ ...alert, show: false })}
          style={{ position: "fixed", top: 16, right: 16, zIndex: 9999, minWidth: 320, boxShadow: "0 4px 12px rgba(0,0,0,.15)" }}>
          {alert.message}
        </Alert>
      )}

      <Row>
        <Col xs={12}>
          <Card style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,.08)" }}>
            <CardBody>
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <CardTitle className="h4 mb-1">
                    <i className="mdi mdi-store-outline me-2" style={{ color: "#556ee6" }} />
                    Sucursales
                  </CardTitle>
                  {empresaNombre && (
                    <p style={{ color: "#6c757d", marginBottom: 0, fontSize: 14 }}>
                      Empresa: <strong>{empresaNombre}</strong>
                    </p>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <Button color="primary" onClick={openCreateModal} style={{ borderRadius: 8 }}>
                    <i className="mdi mdi-plus me-1" /> Nueva Sucursal
                  </Button>
                  <Button color="info" onClick={() => { setImportRows([]); setImportDone(false); setImportModalOpen(true) }} style={{ borderRadius: 8 }}>
                    <i className="mdi mdi-file-import-outline me-1" /> Importar Excel
                  </Button>
                  <Button color="success" onClick={handleExportExcel} disabled={sucursales.length === 0} style={{ borderRadius: 8 }}>
                    <i className="mdi mdi-file-excel-outline me-1" /> Exportar Excel
                  </Button>
                  <Button color="secondary" outline onClick={() => navigate("/portal/consultar-empresas")} style={{ borderRadius: 8 }}>
                    <i className="mdi mdi-arrow-left me-1" /> Volver
                  </Button>
                </div>
              </div>

              {/* Table */}
              {loading ? (
                <div className="text-center py-5"><Spinner color="primary" /><p className="mt-2" style={{ color: "#6c757d" }}>Cargando sucursales...</p></div>
              ) : sucursales.length === 0 ? (
                <div className="text-center py-5">
                  <i className="mdi mdi-store-remove-outline" style={{ fontSize: 48, color: "#c3cbe4" }} />
                  <p className="mt-2" style={{ color: "#6c757d" }}>No hay sucursales registradas</p>
                  <Button color="primary" size="sm" onClick={openCreateModal}>Crear la primera sucursal</Button>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <Table className="table-hover mb-0" style={{ fontSize: 13 }}>
                      <thead style={{ background: "#f8f9fa" }}>
                        <tr>
                          <th>Código</th>
                          <th>Nombre</th>
                          <th>Dirección</th>
                          <th>Teléfono</th>
                          <th>Departamento</th>
                          <th>Ciudad</th>
                          <th>Store ID</th>
                          <th>Brand ID</th>
                          <th>Principal</th>
                          <th>Prefijos Asignados</th>
                          <th>Estado</th>
                          <th style={{ width: 140 }}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedItems.map(suc => (
                          <tr key={suc.id}>
                            <td><strong>{suc.codigo}</strong></td>
                            <td>{suc.nombre}</td>
                            <td>{suc.direccion}</td>
                            <td>{suc.telefono}</td>
                            <td>{suc.departamento}</td>
                            <td>{suc.ciudad}</td>
                            <td>{suc.storeId || "—"}</td>
                            <td>{suc.brandId || "—"}</td>
                            <td>
                              {suc.esPrincipal
                                ? <Badge color="info" pill>Principal</Badge>
                                : <span style={{ color: "#adb5bd", fontSize: 12 }}>—</span>}
                            </td>
                            <td>
                              {(() => {
                                const assigned = prefijosAsignados.filter(a => a.sucursalId === suc.id)
                                if (assigned.length === 0) return <span style={{ color: "#adb5bd", fontSize: 12 }}>—</span>
                                return (
                                  <div className="d-flex flex-wrap gap-1">
                                    {assigned.map(a => (
                                      <Badge key={a.id} color="primary" pill style={{ fontSize: 10 }}>
                                        {a.prefijo}
                                      </Badge>
                                    ))}
                                  </div>
                                )
                              })()}
                            </td>
                            <td>
                              <Badge color={suc.activo ? "success" : "secondary"} pill>
                                {suc.activo ? "Activo" : "Inactivo"}
                              </Badge>
                            </td>
                            <td>
                              <Button color="warning" size="sm" className="me-1" style={{ borderRadius: 6 }}
                                onClick={() => openEditModal(suc)}>
                                <i className="mdi mdi-pencil" />
                              </Button>
                              <Button color="danger" size="sm" style={{ borderRadius: 6 }}
                                disabled={deletingId === suc.id}
                                onClick={() => handleDelete(suc.id, suc.nombre)}>
                                {deletingId === suc.id ? <Spinner size="sm" /> : <i className="mdi mdi-delete" />}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <span style={{ fontSize: 13, color: "#6c757d" }}>
                        Mostrando {(currentPage - 1) * itemsPerPage + 1} a {Math.min(currentPage * itemsPerPage, sucursales.length)} de {sucursales.length}
                      </span>
                      <div className="d-flex gap-1">
                        <Button size="sm" outline disabled={currentPage === 1}
                          onClick={() => setCurrentPage(p => p - 1)} style={{ borderRadius: 6 }}>
                          ← Anterior
                        </Button>
                        {Array.from({ length: totalPages }, (_, i) => (
                          <Button key={i + 1} size="sm" color={currentPage === i + 1 ? "primary" : "light"}
                            onClick={() => setCurrentPage(i + 1)} style={{ borderRadius: 6, minWidth: 36 }}>
                            {i + 1}
                          </Button>
                        ))}
                        <Button size="sm" outline disabled={currentPage === totalPages}
                          onClick={() => setCurrentPage(p => p + 1)} style={{ borderRadius: 6 }}>
                          Siguiente →
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ── Create / Edit Modal ── */}
      <Modal isOpen={modalOpen} toggle={closeModal} size="lg">
        <ModalHeader toggle={closeModal}>
          <i className={`mdi ${modalMode === "create" ? "mdi-plus-circle" : "mdi-pencil"} me-2`} style={{ color: "#556ee6" }} />
          {modalMode === "create" ? "Nueva Sucursal" : "Editar Sucursal"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label style={labelStyle}>Código *</Label>
                <Input style={inputStyle} value={formData.codigo}
                  onChange={e => setFormField("codigo", e.target.value)} placeholder="SUC-001" />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label style={labelStyle}>Nombre *</Label>
                <Input style={inputStyle} value={formData.nombre}
                  onChange={e => setFormField("nombre", e.target.value)} placeholder="Sucursal Principal" />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label style={labelStyle}>Teléfono</Label>
                <Input style={inputStyle} value={formData.telefono}
                  onChange={e => setFormField("telefono", e.target.value)} placeholder="3001234567" />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={8}>
              <FormGroup>
                <Label style={labelStyle}>Dirección</Label>
                <Input style={inputStyle} value={formData.direccion}
                  onChange={e => setFormField("direccion", e.target.value)} placeholder="Calle 80 #10-30" />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label style={labelStyle}>Sucursal Principal</Label>
                <div style={{ paddingTop: 6 }}>
                  <Switch onChange={v => setFormField("esPrincipal", v)} checked={formData.esPrincipal}
                    onColor="#34c38f" offColor="#c3cbe4" height={22} width={44} />
                </div>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label style={labelStyle}>Store ID</Label>
                <Input style={inputStyle} value={formData.storeId}
                  onChange={e => setFormField("storeId", e.target.value)} placeholder="STORE-001" />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label style={labelStyle}>Brand ID</Label>
                <Input style={inputStyle} value={formData.brandId}
                  onChange={e => setFormField("brandId", e.target.value)} placeholder="BRAND-001" />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label style={labelStyle}>Departamento</Label>
                <Input type="select" style={inputStyle} value={formData.departamento}
                  onChange={e => {
                    const depName = e.target.value
                    setFormField("departamento", depName)
                    setFormField("ciudad", "")
                    const dep = departamentos.find(d => d.name === depName)
                    fetchCiudades(dep?.code || "")
                  }}>
                  <option value="">Seleccione</option>
                  {departamentos.map(d => <option key={d.code} value={d.name}>{d.name}</option>)}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label style={labelStyle}>Ciudad</Label>
                <Input type="select" style={inputStyle} value={formData.ciudad}
                  onChange={e => setFormField("ciudad", e.target.value)}
                  disabled={!formData.departamento}>
                  <option value="">Seleccione</option>
                  {ciudades.map(c => <option key={c.code} value={c.name}>{c.name}</option>)}
                </Input>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={closeModal} style={{ borderRadius: 8 }}>
            Cancelar
          </Button>
          <Button color="primary" disabled={saving} onClick={modalMode === "create" ? handleCreate : handleUpdate}
            style={{ borderRadius: 8 }}>
            {saving ? <><Spinner size="sm" /> Guardando...</> : modalMode === "create" ? "Crear Sucursal" : "Guardar Cambios"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── Import Modal ── */}
      <Modal isOpen={importModalOpen} toggle={() => { if (!importing) { setImportModalOpen(false); setImportRows([]) } }} size="xl">
        <ModalHeader toggle={() => { if (!importing) { setImportModalOpen(false); setImportRows([]) } }}>
          <i className="mdi mdi-file-import-outline me-2" style={{ color: "#17a2b8" }} />
          Importar Sucursales desde Excel
        </ModalHeader>
        <ModalBody>
          {/* Template download */}
          <div style={{ background: "#f0f4ff", borderRadius: 8, padding: 12, marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
            <i className="mdi mdi-information-outline" style={{ fontSize: 20, color: "#556ee6" }} />
            <span style={{ fontSize: 13, color: "#495057", flex: 1 }}>
              Asegúrate de que el archivo tenga las columnas: <strong>codigo, nombre, direccion, telefono, departamento, ciudad, storeId, brandId, esPrincipal</strong>
            </span>
            <Button color="primary" size="sm" outline onClick={handleDownloadTemplate} style={{ borderRadius: 6, whiteSpace: "nowrap" }}>
              <i className="mdi mdi-download me-1" /> Descargar Plantilla
            </Button>
          </div>

          {/* File upload area */}
          {importRows.length === 0 && (
            <label style={{
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              border: "2px dashed #c3cbe4", borderRadius: 12, padding: "32px 24px", cursor: "pointer",
              background: "#fafbff", transition: "all 0.2s", marginBottom: 16,
            }}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = "#556ee6"; e.currentTarget.style.background = "#eef2ff" }}
              onDragLeave={e => { e.currentTarget.style.borderColor = "#c3cbe4"; e.currentTarget.style.background = "#fafbff" }}
              onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = "#c3cbe4"; e.currentTarget.style.background = "#fafbff"; const f = e.dataTransfer.files?.[0]; if (f) handleImportFile({ target: { files: [f] } }) }}
            >
              <i className="mdi mdi-cloud-upload-outline" style={{ fontSize: 48, color: "#556ee6", marginBottom: 8 }} />
              <span style={{ fontSize: 15, fontWeight: 600, color: "#495057", marginBottom: 4 }}>Arrastra tu archivo aquí o haz clic para seleccionar</span>
              <span style={{ fontSize: 12, color: "#adb5bd" }}>Formatos aceptados: .xlsx, .xls</span>
              <input type="file" accept=".xlsx,.xls" hidden onChange={handleImportFile} />
            </label>
          )}

          {/* Progress summary */}
          {importRows.length > 0 && (
            <div className="d-flex gap-3 mb-3" style={{ fontSize: 13 }}>
              <Badge color="light" className="px-3 py-2" style={{ fontSize: 13 }}>
                Total: <strong>{importRows.length}</strong>
              </Badge>
              <Badge color="success" className="px-3 py-2" style={{ fontSize: 13 }}>
                <i className="mdi mdi-check-circle me-1" />{importSuccess} exitosas
              </Badge>
              <Badge color="danger" className="px-3 py-2" style={{ fontSize: 13 }}>
                <i className="mdi mdi-alert-circle me-1" />{importErrors} errores
              </Badge>
              {importPending > 0 && (
                <Badge color="secondary" className="px-3 py-2" style={{ fontSize: 13 }}>
                  <i className="mdi mdi-clock-outline me-1" />{importPending} pendientes
                </Badge>
              )}
            </div>
          )}

          {/* Progress bar */}
          {importing && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ background: "#e9ecef", borderRadius: 8, height: 8 }}>
                <div style={{
                  background: "linear-gradient(90deg, #34c38f, #556ee6)",
                  borderRadius: 8,
                  height: 8,
                  width: `${((importSuccess + importErrors) / importRows.length) * 100}%`,
                  transition: "width 0.3s",
                }} />
              </div>
            </div>
          )}

          {/* Results table */}
          {importRows.length > 0 && (
            <div className="table-responsive" style={{ maxHeight: 400, overflowY: "auto" }}>
              <Table size="sm" className="mb-0" style={{ fontSize: 12 }}>
                <thead style={{ background: "#f8f9fa", position: "sticky", top: 0 }}>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Código</th>
                    <th>Nombre</th>
                    <th>Dirección</th>
                    <th>Departamento</th>
                    <th>Ciudad</th>
                    <th style={{ width: 50 }}>Ppal</th>
                    <th style={{ width: 120 }}>Estado</th>
                    <th>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {importRows.map(row => (
                    <tr key={row.index} style={{
                      background: row.status === "success" ? "#d4edda" : row.status === "error" ? "#f8d7da" : row.status === "importing" ? "#fff3cd" : "white",
                      transition: "background 0.3s",
                    }}>
                      <td>{row.index}</td>
                      <td><strong>{row.data.codigo}</strong></td>
                      <td>{row.data.nombre}</td>
                      <td>{row.data.direccion}</td>
                      <td>{row.data.departamento}</td>
                      <td>{row.data.ciudad}</td>
                      <td style={{ textAlign: "center" }}>{row.data.esPrincipal ? "✓" : "—"}</td>
                      <td>
                        {row.status === "pending" && <Badge color="secondary" pill>Pendiente</Badge>}
                        {row.status === "importing" && <Badge color="warning" pill><Spinner size="sm" style={{ width: 10, height: 10 }} /> Importando</Badge>}
                        {row.status === "success" && <Badge color="success" pill><i className="mdi mdi-check" /> Exitoso</Badge>}
                        {row.status === "error" && <Badge color="danger" pill><i className="mdi mdi-close" /> Error</Badge>}
                      </td>
                      <td style={{ fontSize: 11, color: row.status === "error" ? "#dc3545" : "#6c757d" }}>
                        {row.message}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline disabled={importing} onClick={() => { setImportModalOpen(false); setImportRows([]) }} style={{ borderRadius: 8 }}>
            {importDone ? "Cerrar" : "Cancelar"}
          </Button>
          {!importDone && (
            <Button color="primary" disabled={importing || importRows.length === 0} onClick={handleStartImport} style={{ borderRadius: 8 }}>
              {importing ? <><Spinner size="sm" /> Importando...</> : <><i className="mdi mdi-upload me-1" /> Iniciar Importación ({importRows.length} filas)</>}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </React.Fragment>
  )
}

const mapStateToProps = state => ({
  user: state.Login.user,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(Sucursales)
